import React, { useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Menu, Skeleton, TableContainer, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { alpha } from '@mui/material/styles';
import { SummaryMetric } from './SummaryStrip';

const asArray = (sx) => (Array.isArray(sx) ? sx : [sx]);

export function ActionToolbar({ children, sx, className = '', direction, spacing, alignItems, justifyContent, useFlexGap, ...props }) {
  return (
    <Box {...props} className={`workspace-actions ${className}`} sx={[
      ...asArray(sx),
      { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: { xs: 'stretch', sm: 'flex-end' },
        gap: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' },
        '& > .MuiButton-root, & > span': { width: { xs: '100%', sm: 'auto' }, minWidth: 0, maxWidth: '100%' },
        '& .MuiButton-root': { minHeight: 40, whiteSpace: 'normal', overflowWrap: 'anywhere' },
      },
    ]}>{children}</Box>
  );
}

// The original buttons remain the actions, including their disabled/loading
// state and handlers. Only the less frequent actions move into this menu.
export function OverflowActions({ children, label = 'More actions' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  return <>
    <Button variant="outlined" startIcon={<MoreHorizIcon />} aria-haspopup="menu"
      aria-expanded={Boolean(anchorEl)} onClick={(event) => setAnchorEl(event.currentTarget)}>{label}</Button>
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
      {React.Children.map(children, (child) => React.isValidElement(child) ? React.cloneElement(child, {
        role: 'menuitem', variant: 'text', fullWidth: true,
        sx: { display: 'flex', justifyContent: 'flex-start', borderRadius: 0, px: 2, minHeight: 44 },
        onClick: (event) => { setAnchorEl(null); child.props.onClick?.(event); },
      }) : child)}
    </Menu>
  </>;
}

export function PageHeader({ title, description, actions, children, className = '', sx, ...props }) {
  return (
    <Box component="header" {...props} className={`workspace-page-header ${className}`} sx={[
      ...asArray(sx),
      { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, mb: 3, minWidth: 0 },
    ]}>
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography component="h1" variant="h4" sx={{ fontSize: { xs: '1.375rem', sm: '1.625rem' }, fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.025em', overflowWrap: 'anywhere' }}>{title}</Typography>
        {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720, lineHeight: 1.6 }}>{description}</Typography>}
        {children}
      </Box>
      {actions && <ActionToolbar sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, maxWidth: '100%' }}>{actions}</ActionToolbar>}
    </Box>
  );
}

// Consume former Stack/Grid props when upgrading an existing filter panel.
export function FilterBar({ children, sx, className = '', label = 'Search and filters',
  direction, spacing, alignItems, justifyContent, container, item, elevation, variant, ...props }) {
  return (
    <Box component="section" aria-label={label} {...props} className={`workspace-filters ${className}`} sx={[
      ...asArray(sx),
      { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, minWidth: 0, maxWidth: '100%', p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper', boxShadow: 'none',
        '& > .MuiCardContent-root, & > .MuiCardContent-root:last-child': { p: 0, width: '100%' },
        '& > .MuiTextField-root, & > .MuiFormControl-root': { width: { xs: '100%', sm: 'auto' }, minWidth: { xs: 0, sm: 180 } },
      },
    ]}>{children}</Box>
  );
}

// Keep all columns available on small screens; sorting and pagination stay with
// the original table. The scroll region is also reachable from the keyboard.
export function DataTable({ children, sx, label = 'Data table', ...props }) {
  return <TableContainer role="region" aria-label={label} tabIndex={0} {...props} sx={[
    ...asArray(sx),
    { maxWidth: '100%', minWidth: 0, overflowX: 'auto', overscrollBehaviorX: 'contain', boxShadow: 'none',
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
      '@media print': { overflow: 'visible', maxHeight: 'none' },
    },
  ]}>{children}</TableContainer>;
}

export function EmptyState({ title = 'No records yet', description, action, children, sx }) {
  return <Box sx={[{ py: 5, px: 2, textAlign: 'center', minWidth: 0 }, ...asArray(sx)]}>
    <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
    {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{description}</Typography>}
    {children}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>;
}

export function LoadingState({ label = 'Loading records…' }) {
  return <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
    <CircularProgress size={24} aria-label={label} />
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Box>;
}

export function StatCard({ title, value, icon, description, color = 'primary', loading = false, error, ...props }) {
  return <SummaryMetric {...props}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      {icon && <Avatar sx={(theme) => ({ width: 36, height: 36, borderRadius: '10px', bgcolor: alpha(theme.palette[color].main, 0.1), color: `${color}.main` })}>{icon}</Avatar>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>{title}</Typography>
        {loading ? <Skeleton width="65%" height={32} aria-label={`Loading ${title}`} /> : error ? <Alert severity="error" sx={{ p: 0 }}>{error}</Alert> : <Typography variant="h4" component="p" sx={{ overflowWrap: 'anywhere' }}>{value ?? '—'}</Typography>}
        {description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>{description}</Typography>}
      </Box>
    </Box>
  </SummaryMetric>;
}
